/* eslint-disable arrow-body-style */
import { ComponentPropsWithoutRef, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import ErrorMessage from './ErrorMessage'
import { containsReservedCharacters, getInputRules, validateField } from '../../utils/form-utils'
import { textinputMaxLength } from '../../constants'
import Input from './TextInput'
import { ErrorMessages, Rules } from '../../config/types'
import Details from './Details'

type Props = {
    name: string
    title: string
    width?: 'full' | 'three-quarters' | 'two-thirds' | 'one-half' | 'one-third' | 'one-quarter'
    rules?: Rules
    txt: string
    errorMessages?: ErrorMessages
    detailsHeading?: string
    detailsContent?: string
    inputHeading?: string
} & ComponentPropsWithoutRef<'input'>

const InstanceInputField = (props: Props) => {
    const { name, title, rules, disabled, defaultValue, errorMessages, detailsHeading, detailsContent, inputHeading } = props
    const { register, getFieldState, getValues, setValue } = useFormContext()
    let type = 'text'
    if (rules?.number) type = 'number'
    else if (rules?.email) type = 'email'

    const inputRules = useMemo(() => ({ ...getInputRules(rules ?? {}, title, textinputMaxLength) }), [rules, title])

    useEffect(() => {
        if (getValues(name) === undefined) {
            setValue(name, defaultValue ?? '')
        }
    }, [])

    const hooksFormRegister = register(name, {
        validate: (value: string) => validateField(title, inputRules, value, errorMessages)
    })
    const { error } = getFieldState(name)

    return (
        <>
            {error && <ErrorMessage error={error} />}
            {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

            <div className='govuk-form-group'>
                {inputHeading && (
                    <label className='govuk-label' htmlFor='instances'>
                        {inputHeading}
                    </label>
                )}
                <div className='govuk-input__wrapper'>
                    <Input
                        {...props}
                        {...hooksFormRegister}
                        disabled={disabled ?? false}
                        onKeyDown={e => {
                            // The e.key values are strings for control characters such as 'Backspace'
                            // All non control characters will have a single character length
                            if (e.key.length === 1 && containsReservedCharacters(e.key, inputRules.reservedCharacters))
                                e.preventDefault()
                        }}
                        onPaste={e => {
                            const clipboardData = e.clipboardData.getData('text')
                            if (containsReservedCharacters(clipboardData, inputRules.reservedCharacters))
                                e.preventDefault()
                        }}
                        numeric={!!rules?.number}
                        width='one-quarter'
                        type={type}
                    />
                    <div className='govuk-input__suffix' aria-hidden='true'>
                        Instances
                    </div>
                </div>
            </div>
        </>
    )
}
InstanceInputField.displayName = 'InstanceInputField'

export default InstanceInputField
